// ══════════════════════════════════════════════════════
// PeptideVault v6.5 — AI-Assisted Master Plan Import
// Deterministic xlsx → protocol drafts.
// AI may suggest names/mappings but is fully optional.
// Uses parseXlsxNative from vendors.js.
// ══════════════════════════════════════════════════════

import { inheritProtocolDisplayPreferences } from './protocol-views.js'

var _mpiStep = 0           // 0=drop, 1=parsing, 2=summary, 3=review, 4=applied
var _mpiRows = null        // raw parsed rows
var _mpiDetected = null    // { dateCol, compoundCols, episodes, anomalies }
var _mpiDrafts = []        // protocol drafts for review
var _mpiMappings = {}      // header→peptideId mappings
var _mpiMappingMeta = {}   // header→{ method:'Exact|Alias|Fuzzy|AI|Manual', confidence:0..1 }
var _mpiIncluded = {}      // draft id→ include boolean
var _mpiFileName = ''
var _mpiError = ''
var _mpiAIMappings = []     // AI-suggested mappings for unresolved headers
var _mpiAICombinations = [] // AI-detected combination protocols
var _mpiAIAnomalies = []    // AI-detected anomalies
var _mpiAINames = []        // AI-suggested protocol names
var _mpiAISummary = ''
  _mpiAINotes = []
  _mpiAIRunning = false; _mpiAIDone = false
var _mpiAINotes = []
  _mpiAIRunning = false; _mpiAIDone = false
var _mpiGroupingRules = [] // Persisted: [{compounds:['a','b'], createdAt:'...'}]      // AI-generated import summary
var _mpiImpactAcknowledged = false

function resetMasterPlanImport() {
  _mpiStep = 0; _mpiRows = null; _mpiDetected = null; _mpiDrafts = []
  _mpiMappings = {}; _mpiMappingMeta = {}; _mpiIncluded = {}; _mpiFileName = ''; _mpiError = ''
  _mpiAIMappings = []; _mpiAICombinations = []; _mpiAIAnomalies = []; _mpiAINames = []; _mpiAISummary = ''
  _mpiImpactAcknowledged = false
  render()
}

function renderMasterPlanImportTab() {
  if (_mpiStep === 0) return renderMPIDropZone()
  if (_mpiStep === 1) return renderMPIParsing()
  if (_mpiStep === 2) return renderMPISummary()
  if (_mpiStep === 3) return renderMPIReview()
  if (_mpiStep === 4) return renderMPIApplied()
  return renderMPIDropZone()
}

function setMPIStep(step) {
  var next = Number(step)
  if (![0,1,2,3,4].includes(next)) return
  _mpiStep = next
  render()
}

// ══════════════════════════════════════════════════════
// STEP 0: File drop / selection
// ══════════════════════════════════════════════════════


// P1-4: Batched AI analysis — called on user request, not automatically
var _mpiAIRunning = false
var _mpiAIDone = false

async function runMPIAIAnalysis() {
  if (_mpiAIRunning || !_mpiDrafts || _mpiDrafts.length === 0) return
  if (typeof hasAnthropicCredential === 'function' && !hasAnthropicCredential()) { showToast('AI not configured'); return }
  _mpiAIRunning = true; render()

  // Collect unmapped headers
  var unmappedHeaders = []
  _mpiDrafts.forEach(function(d) {
    if (mpiDraftResolution(d).kind === 'unmapped' && unmappedHeaders.indexOf(d.compoundName) < 0) unmappedHeaders.push(d.compoundName)
  })

  // AI mapping (only for unresolved)
  if (unmappedHeaders.length > 0 && typeof aiMapCompounds === 'function') {
    var aiResult = await aiMapCompounds(unmappedHeaders, S.peptides || [], S.peptideLibrary || [], S.blends || [])
    if (aiResult.ok && aiResult.matches) { _mpiAIMappings = aiResult.matches; render() }
  }

  // Combination + anomaly + naming + notes + summary — one batch
  if (typeof aiDetectCombinations === 'function') {
    var combResult = await aiDetectCombinations(_mpiDrafts)
    if (combResult.ok && combResult.groups) _mpiAICombinations = combResult.groups
  }
  if (typeof aiDetectAnomalies === 'function') {
    var anomResult = await aiDetectAnomalies(_mpiDrafts)
    if (anomResult.ok && anomResult.anomalies) _mpiAIAnomalies = anomResult.anomalies
  }
  if (typeof aiSuggestProtocolNames === 'function') {
    var nameResult = await aiSuggestProtocolNames(_mpiDrafts)
    if (nameResult.ok && nameResult.suggestions) _mpiAINames = nameResult.suggestions
  }

  // Note interpretation
  var noteTexts = []
  var rows = _mpiRows || []
  var detected = _mpiDetected || {}
  var noteCol = (detected.headers || []).findIndex(function(h) { return (h||'').toLowerCase() === 'notes' || (h||'').toLowerCase() === 'note' })
  if (noteCol >= 0) {
    for (var nr = 1; nr < Math.min(rows.length, 200); nr++) {
      var nv = (rows[nr] && rows[nr][noteCol] || '').toString().trim()
      if (nv && nv.length > 3) noteTexts.push(nv)
    }
  }
  if (noteTexts.length > 0 && typeof aiInterpretNotes === 'function') {
    var compNames = _mpiDrafts.map(function(d) { return d.compoundName })
    var allDates = []
    _mpiDrafts.forEach(function(d) { d.sourceExactSchedule.slice(0, 5).forEach(function(dt) { allDates.push(dt) }) })
    var noteResult = await aiInterpretNotes(noteTexts.slice(0, 20), compNames, allDates)
    if (noteResult.ok && noteResult.interpretations) _mpiAINotes = noteResult.interpretations
  }

  // Summary
  if (typeof aiImportSummary === 'function') {
    var sumResult = await aiImportSummary(detected, _mpiDrafts, _mpiMappings)
    if (sumResult.ok) _mpiAISummary = sumResult.summary
  }

  _mpiAIRunning = false; _mpiAIDone = true; render()
}

function renderMPIDropZone() {
  return '<div class="tab-content" style="animation:fadeIn .25s ease">' +
    renderMPIWorkflow('Upload') +
    '<div class="row" style="margin-bottom:16px"><h2 style="font-size:20px;font-weight:700">Master Plan Import</h2></div>' +
    '<div class="card" style="padding:48px;text-align:center">' +
      '<div style="font-size:48px;margin-bottom:12px">\uD83D\uDCCA</div>' +
      '<p style="font-size:16px;font-weight:600">Import Master Plan from Excel</p>' +
      '<p style="font-size:13px;color:var(--text3);max-width:480px;margin:8px auto 20px">Drop or choose a .xlsx file with dates in the first column and compound doses in subsequent columns. PeptideVault will deterministically parse protocols, detect phases, breaks, and episodes.</p>' +
      (_mpiError ? '<div style="color:var(--red);font-size:13px;margin-bottom:12px">' + esc(_mpiError) + '</div>' : '') +
      '<button class="btn btn-primary" onclick="triggerMPIFileSelect()">' + ICONS.plus + ' Choose .xlsx File</button>' +
      '<input type="file" id="mpi-file-input" accept=".xlsx,.csv" style="display:none" onchange="handleMPIFile(this)">' +
    '</div>' +
    renderMPIExistingImports() +
  '</div>'
}

function renderMPIExistingImports() {
  var imported = (S.protocols || []).filter(function(p) { return p.sourceType === 'excel-master-plan' })
  if (imported.length === 0) return ''
  var sources = {}
  imported.forEach(function(p) {
    var key = p.sourceWorkbookFingerprint || 'unknown'
    if (!sources[key]) sources[key] = { name: p.sourceSheetName || p.sourceColumnName || 'Unknown', count: 0, importedAt: p.sourceImportedAt || '' }
    sources[key].count++
  })
  return '<div class="card" style="margin-top:16px;padding:14px">' +
    '<div style="font-size:11px;font-weight:700;color:var(--text4);text-transform:uppercase;margin-bottom:8px">Previously Imported</div>' +
    Object.keys(sources).map(function(key) {
      var s = sources[key]
      return '<div style="font-size:12px;padding:4px 0">' + esc(s.name) + ' \u2014 ' + s.count + ' protocol(s)' + (s.importedAt ? ' \u00B7 imported ' + s.importedAt.slice(0, 10) : '') + '</div>'
    }).join('') +
  '</div>'
}

function triggerMPIFileSelect() {
  var input = document.getElementById('mpi-file-input')
  if (input) input.click()
}

async function handleMPIFile(input) {
  var file = input.files && input.files[0]
  if (!file) return
  _mpiFileName = file.name
  _mpiStep = 1
  _mpiError = ''
  render()

  try {
    var data = await file.arrayBuffer()
    var rows
    if (file.name.endsWith('.csv')) {
      var text = new TextDecoder().decode(data)
      rows = text.trim().split('\n').map(function(line) {
        return line.split(',').map(function(c) { return c.replace(/^"|"$/g, '').trim() })
      })
    } else {
      rows = await parseXlsxNative(new Uint8Array(data))
    }

    if (!rows || rows.length < 2) {
      _mpiError = 'File has no usable data rows.'
      _mpiStep = 0
      render()
      return
    }

    _mpiRows = rows
    var detected = detectMasterPlanStructure(rows)
    detected.sheetName = file.name.endsWith('.csv') ? 'CSV' : 'Sheet1'
    _mpiDetected = detected

    if (detected.dateCol < 0) {
      _mpiError = 'Could not find a date column. First column should contain dates (YYYY-MM-DD or Excel serial numbers).'
      _mpiStep = 0
      render()
      return
    }

    // Build protocol drafts
    _mpiDrafts = buildProtocolDrafts(rows, detected)

    // Auto-map to existing peptides (deterministic first)
    _mpiMappings = {}
    _mpiMappingMeta = {}
    _mpiIncluded = {}
    var unmappedHeaders = []
    _mpiDrafts.forEach(function(d) {
      _mpiIncluded[d.id] = true
      // Supplement columns belong to the supplement activation workflow, even
      // if a peptide/library record happens to have the same display name.
      var supplementColumn = mpiIsSharedSupplementSchedule(d.compoundName) || mpiExactSupplement(d.compoundName)
      var mapped = supplementColumn ? null : autoMapCompoundDetailed(d.compoundName)
      if (mapped && mapped.id) { _mpiMappings[d.compoundName] = mapped.id; _mpiMappingMeta[d.compoundName] = { method:mapped.method, confidence:mapped.confidence } }
      else if (!supplementColumn && unmappedHeaders.indexOf(d.compoundName) < 0) unmappedHeaders.push(d.compoundName)
    })

    // Apply saved grouping rules deterministically
    if (typeof applyGroupingRules === "function") applyGroupingRules()

    _mpiStep = 2
    render()

  } catch (e) {
    _mpiError = 'Parse error: ' + (e.message || e)
    _mpiStep = 0
    render()
  }
}

// ══════════════════════════════════════════════════════
// DETERMINISTIC PARSING
// ══════════════════════════════════════════════════════

function detectMasterPlanStructure(rows) {
  var headers = rows[0] || []
  var dateCol = -1
  var compoundCols = []

  // Detect date column (usually first, look for 'date' or date-like values)
  for (var c = 0; c < headers.length; c++) {
    var h = (headers[c] || '').toString().toLowerCase()
    if (h === 'date' || h === 'day' || h.indexOf('date') >= 0) { dateCol = c; break }
  }
  // Fallback: first column with date-like values
  if (dateCol < 0) {
    for (var c2 = 0; c2 < Math.min(3, headers.length); c2++) {
      var dateCount = 0
      for (var r = 1; r < Math.min(20, rows.length); r++) {
        if (parseMPDate(rows[r][c2])) dateCount++
      }
      if (dateCount >= 3) { dateCol = c2; break }
    }
  }

  // Compound columns: everything except date/day columns
  // P0-3 FIX: scan entire column, not just first 30 rows. Stop at first valid dose per column.
  for (var c3 = 0; c3 < headers.length; c3++) {
    if (c3 === dateCol) continue
    var hdr = (headers[c3] || '').toString().trim()
    if (!hdr) continue
    var h2 = hdr.toLowerCase()
    if (h2 === 'day' || h2 === 'day #' || h2 === 'notes' || h2 === 'week') continue
    // Scan full column for at least one parseable dose. Shared supplement
    // schedules use Yes/X markers instead of a numeric dose.
    var found = false
    var firstRow = -1
    for (var r2 = 1; r2 < rows.length; r2++) {
      var v = (rows[r2] && rows[r2][c3] || '').toString().trim()
      if (v && (parseDoseCell(v) || (mpiIsSharedSupplementSchedule(hdr) && parseSupplementScheduleCell(v)))) { found = true; firstRow = r2; break }
    }
    if (found) {
      compoundCols.push({ col: c3, name: hdr, firstDataRow: firstRow })
    }
  }

  // Detect anomalies
  var anomalies = []
  if (dateCol < 0) anomalies.push('No date column detected')
  if (compoundCols.length === 0) anomalies.push('No compound columns detected')

  return {
    dateCol: dateCol,
    compoundCols: compoundCols,
    totalRows: rows.length - 1,
    anomalies: anomalies,
    headers: headers
  }
}

function parseSupplementScheduleCell(val) {
  var raw = String(val == null ? '' : val).trim()
  if (!raw) return null
  var normalized = raw.toLowerCase()
  if (!['yes','y','x','active','on','true','1'].includes(normalized)) return null
  return { rawValue:raw, numericValue:1, unit:'schedule', components:null, timedDoses:null, isScheduleMarker:true }
}

function parseMPDate(val) {
  if (!val) return null
  var s = val.toString().trim()
  // ISO date
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  // US date MM/DD/YYYY
  var us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (us) return us[3] + '-' + us[1].padStart(2, '0') + '-' + us[2].padStart(2, '0')
  // Excel serial date (number > 30000 is plausible)
  var n = parseFloat(s)
  if (!isNaN(n) && n > 30000 && n < 100000) {
    // Excel epoch is 1899-12-30
    var d = new Date(Date.UTC(1899, 11, 30 + n))
    return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0') + '-' + String(d.getUTCDate()).padStart(2, '0')
  }
  return null
}

function parseDoseCell(val) {
  if (!val) return null
  var s = val.toString().trim()
  if (!s || s === '-' || s === '0') return null
  // A slash can separate timed administrations rather than blend components.
  // Preserve each administration so "1mg AM/1mg PM" becomes two events.
  var timedDoses = parseTimedDoseCell(s)
  // P1-2: Detect slash-delimited blend doses (e.g. "221.6mcg/224.2mcg/200mcg/100mcg")
  var components = null
  if (!timedDoses && s.indexOf('/') > 0 && s.split('/').length >= 2) {
    var parts = s.split('/')
    var allValid = true
    components = []
    for (var pi = 0; pi < parts.length; pi++) {
      var partStr = parts[pi].trim()
      var partNum = parseFloat(partStr.replace(/[^0-9.\-]/g, ''))
      if (isNaN(partNum) || partNum <= 0) { allValid = false; break }
      var partUnitMatch = partStr.match(/(mcg|mg|mL|cc|iu|units?|sprays?|drops?)/i)
      var partUnit = normalizeProtocolDoseUnit(partUnitMatch ? partUnitMatch[1] : null)
      components.push({ numericValue: partNum, unit: partUnit, raw: partStr })
    }
    if (!allValid) components = null
  }
  var num = timedDoses && timedDoses.length ? timedDoses[0].numericValue : parseFloat(s.replace(/[^0-9.\-]/g, ''))
  if (isNaN(num) || num <= 0) return null
  // Extract unit if present
  var unitMatch = s.match(/(mcg|mg|mL|cc|iu|units?|sprays?|drops?)/i)
  var unit = normalizeProtocolDoseUnit(unitMatch ? unitMatch[1] : null)
  return { rawValue: s, numericValue: num, unit: unit, components: components, timedDoses: timedDoses }
}

function normalizeProtocolDoseUnit(unit) {
  if (!unit) return null
  var value = String(unit).toLowerCase()
  if (value === 'iu') return 'IU'
  if (value === 'unit' || value === 'units') return 'units'
  if (value === 'ml' || value === 'cc') return 'mL'
  if (value === 'spray' || value === 'sprays') return 'sprays'
  if (value === 'drop' || value === 'drops') return 'drops'
  return value
}

function parseTimedDosePart(part) {
  var raw = String(part||'').replace(/^\s*[/+,;:-]+\s*|\s*[/+,;:-]+\s*$/g,'').trim()
  if (!raw) return null
  var pieces = raw.split(/\s*\/\s*/).filter(Boolean), components = []
  for (var i=0;i<pieces.length;i++) {
    var numberMatch=pieces[i].match(/\d+(?:\.\d+)?/), unitMatch=pieces[i].match(/(mcg|mg|mL|cc|iu|units?|sprays?|drops?)/i)
    if (!numberMatch) return null
    components.push({numericValue:parseFloat(numberMatch[0]),unit:normalizeProtocolDoseUnit(unitMatch?unitMatch[1]:null),raw:pieces[i].trim()})
  }
  var sharedUnit=(components.find(function(c){return c.unit})||{}).unit||null
  components.forEach(function(c){if(!c.unit)c.unit=sharedUnit})
  return {numericValue:components[0].numericValue,unit:components[0].unit||sharedUnit,components:components.length>1?components:null,raw:raw}
}

function parseTimedDoseCell(val) {
  var s = (val == null ? '' : String(val)).trim()
  if (!s || !/(?:\bAM\b|\bPM\b|morning|evening)/i.test(s)) return null
  var doses = [], markerAfter=/([\s\S]*?)\b(AM|PM|morning|evening)\b/gi, match
  while ((match=markerAfter.exec(s))) {
    var parsed=parseTimedDosePart(match[1]), period=match[2].toLowerCase()
    if (!parsed) { doses=[]; break }
    var timeLabel=period==='am'||period==='morning'?'AM':'PM'
    doses.push({numericValue:parsed.numericValue,unit:parsed.unit,components:parsed.components,time:timeLabel==='AM'?'07:00':'19:00',timeLabel:timeLabel,raw:parsed.raw+' '+timeLabel})
  }
  if (doses.length < 2) {
    doses=[]
    var markers=[], markerRe=/\b(AM|PM|morning|evening)\b/gi
    while((match=markerRe.exec(s)))markers.push({label:match[1],start:match.index,end:markerRe.lastIndex})
    for(var i=0;i<markers.length;i++){
      var parsedAfter=parseTimedDosePart(s.slice(markers[i].end,i+1<markers.length?markers[i+1].start:s.length)),periodAfter=markers[i].label.toLowerCase()
      if(!parsedAfter){doses=[];break}
      var labelAfter=periodAfter==='am'||periodAfter==='morning'?'AM':'PM'
      doses.push({numericValue:parsedAfter.numericValue,unit:parsedAfter.unit,components:parsedAfter.components,time:labelAfter==='AM'?'07:00':'19:00',timeLabel:labelAfter,raw:labelAfter+' '+parsedAfter.raw})
    }
  }
  if (doses.length < 2) return null
  // Carry a stated unit across entries such as "1mg AM/1 PM".
  var sharedUnit = (doses.find(function(d) { return d.unit }) || {}).unit || null
  doses.forEach(function(d) { if (!d.unit) d.unit = sharedUnit })
  return doses
}

function buildProtocolDrafts(rows, detected) {
  var drafts = []
  var dateCol = detected.dateCol

  detected.compoundCols.forEach(function(cc) {
    // Collect date→dose pairs
    var entries = []
    for (var r = 1; r < rows.length; r++) {
      var dateStr = parseMPDate(rows[r][dateCol])
      if (!dateStr) continue
      var dose = parseDoseCell(rows[r][cc.col])
      if (!dose && mpiIsSharedSupplementSchedule(cc.name)) dose = parseSupplementScheduleCell(rows[r][cc.col])
      if (!dose) continue
      if (dose.timedDoses && dose.timedDoses.length) {
        dose.timedDoses.forEach(function(td, administrationIndex) {
          entries.push({
            date: dateStr,
            time: td.time,
            timeLabel: td.timeLabel,
            administrationIndex: administrationIndex,
            dose: { rawValue: td.raw, numericValue: td.numericValue, unit: td.unit || dose.unit, components: td.components || null, timedDoses: null },
            sourceRawCell: dose.rawValue,
            sourceRow:r + 1,
            sourceColumn:cc.col + 1
          })
        })
      } else {
        entries.push({ date: dateStr, dose: dose, sourceRawCell: dose.rawValue, sourceRow:r + 1, sourceColumn:cc.col + 1 })
      }
    }

    if (entries.length === 0) return
    entries.sort(function(a, b) { return a.date.localeCompare(b.date) })

    // Split into episodes (gap > expected cadence * 3)
    var episodes = splitEpisodes(entries)

    episodes.forEach(function(episode, epIdx) {
      // Detect phases (dose changes within episode)
      var phases = detectPhases(episode)
      // Detect frequency
      var frequency = detectFrequency(episode)
      // Detect breaks
      var breaks = detectBreaks(episode, frequency)

      var startDate = episode[0].date
      var endDate = episode[episode.length - 1].date
      var primaryDose = episode[0].dose

      var draft = {
        id: uid(),
        compoundName: cc.name,
        columnIndex: cc.col,
        episodeIndex: epIdx,
        startDate: startDate,
        endDate: endDate,
        frequency: frequency.code,
        doseAmount: primaryDose.numericValue,
        doseUnit: primaryDose.unit || 'mcg',
        phases: phases,
        breaks: breaks,
        totalDoses: episode.length,
        entries: episode,
        detectedFrequency: frequency,
        sourceExactSchedule: episode.map(function(e) { return e.date }),
        sourceRawDoseCells: episode.map(function(e) { return e.dose.rawValue })
      }
      drafts.push(draft)
    })
  })

  return drafts
}

function splitEpisodes(entries) {
  if (entries.length <= 1) return [entries]
  // Compute median gap
  var gaps = []
  for (var i = 1; i < entries.length; i++) {
    var gap = daysBetween(entries[i - 1].date, entries[i].date)
    if (gap > 0) gaps.push(gap)
  }
  gaps.sort(function(a, b) { return a - b })
  var medianGap = gaps[Math.floor(gaps.length / 2)] || 1
  var threshold = Math.max(medianGap * 3, 14) // gap > 3× median or 14 days = new episode

  var episodes = []
  var current = [entries[0]]
  for (var j = 1; j < entries.length; j++) {
    var gap = daysBetween(entries[j - 1].date, entries[j].date)
    if (gap > threshold) {
      episodes.push(current)
      current = []
    }
    current.push(entries[j])
  }
  if (current.length > 0) episodes.push(current)
  return episodes
}

function detectFrequency(entries) {
  var administrationsByDate = {}
  entries.forEach(function(e) { administrationsByDate[e.date] = (administrationsByDate[e.date] || 0) + 1 })
  var maxAdministrations = Math.max.apply(null, Object.keys(administrationsByDate).map(function(date) { return administrationsByDate[date] }))
  var uniqueEntries = entries.filter(function(e, i) { return i === 0 || e.date !== entries[i - 1].date })
  if (uniqueEntries.length < 2) return { code: 'custom', label: maxAdministrations > 1 ? maxAdministrations + '\u00d7/Day' : 'Custom', avgGap: 0, administrationsPerDay:maxAdministrations }
  var gaps = []
  for (var i = 1; i < uniqueEntries.length; i++) {
    gaps.push(daysBetween(uniqueEntries[i - 1].date, uniqueEntries[i].date))
  }
  var avg = gaps.reduce(function(s, g) { return s + g }, 0) / gaps.length

  if (avg <= 1.2) return { code: 'daily', label: maxAdministrations > 1 ? maxAdministrations + '\u00d7/Day' : 'Daily', avgGap: avg, administrationsPerDay:maxAdministrations }
  if (avg >= 1.8 && avg <= 2.5) return { code: 'eod', label: 'Every Other Day', avgGap: avg }
  if (avg >= 2.0 && avg <= 2.8) {
    // Check if MWF pattern
    var dows = uniqueEntries.map(function(e) { return parseLD(e.date).getDay() })
    var mwf = dows.filter(function(d) { return d === 1 || d === 3 || d === 5 }).length / dows.length
    if (mwf > 0.8) return { code: '3x-week', label: '3\u00d7/Week', avgGap: avg }
  }
  if (avg >= 6 && avg <= 8) return { code: 'weekly', label: 'Weekly', avgGap: avg }
  if (avg >= 13 && avg <= 15) return { code: 'biweekly', label: 'Biweekly', avgGap: avg }
  return { code: 'custom', label: 'Custom (~' + avg.toFixed(1) + 'd)', avgGap: avg }
}

function detectPhases(entries) {
  if (entries.length < 3) return []
  if (entries.some(function(e,i){return i>0&&e.date===entries[i-1].date})) return []
  var phases = []
  var currentDose = entries[0].dose.numericValue
  var phaseStartIdx = 0
  var startDate = entries[0].date // P1-3: use actual dates for phase boundaries
  for (var i = 1; i < entries.length; i++) {
    if (entries[i].dose.numericValue !== currentDose) {
      if (i - phaseStartIdx >= 2) {
        phases.push({
          name: 'Phase ' + (phases.length + 1),
          startDay: daysBetween(startDate, entries[phaseStartIdx].date),
          endDay: daysBetween(startDate, entries[i - 1].date),
          startDate: entries[phaseStartIdx].date,
          endDate: entries[i - 1].date,
          doseAmount: currentDose,
          doseUnit: entries[phaseStartIdx].dose.unit || 'mcg'
        })
      }
      currentDose = entries[i].dose.numericValue
      phaseStartIdx = i
    }
  }
  // Close last phase
  if (phases.length > 0 && entries.length - phaseStartIdx >= 2) {
    var startDate2 = entries[0].date
    phases.push({
      name: 'Phase ' + (phases.length + 1),
      startDay: daysBetween(startDate2, entries[phaseStartIdx].date),
      endDay: daysBetween(startDate2, entries[entries.length - 1].date),
      startDate: entries[phaseStartIdx].date,
      endDate: entries[entries.length - 1].date,
      doseAmount: currentDose,
      doseUnit: entries[phaseStartIdx].dose.unit || 'mcg'
    })
  }
  return phases.length > 1 ? phases : [] // Only return if multiple phases detected
}

function detectBreaks(entries, frequency) {
  if (entries.length < 3 || frequency.avgGap <= 0) return []
  var breaks = []
  var expectedGap = frequency.avgGap
  var breakThreshold = expectedGap * 2.5

  for (var i = 1; i < entries.length; i++) {
    var gap = daysBetween(entries[i - 1].date, entries[i].date)
    if (gap > breakThreshold) {
      breaks.push({
        startDate: fmtLD(addDays(parseLD(entries[i - 1].date), 1)),
        endDate: fmtLD(addDays(parseLD(entries[i].date), -1)),
        reason: 'Detected gap (' + gap + ' days)'
      })
    }
  }
  return breaks
}

// ══════════════════════════════════════════════════════
// COMPOUND NAME RESOLUTION
// ══════════════════════════════════════════════════════

function autoMapCompound(name) {
  var result = autoMapCompoundDetailed(name)
  return result && result.id || null
}

function autoMapCompoundDetailed(name) {
  if (!name) return null
  var lower = name.toLowerCase().trim()

  // Exact match on peptide name
  var exact = (S.peptides || []).find(function(p) { return (p.name || '').toLowerCase() === lower })
  if (exact) return { id:exact.id, method:'Exact', confidence:1 }

  // Library alias match
  var libMatch = (S.peptideLibrary || []).find(function(l) {
    if ((l.scientificName || '').toLowerCase() === lower) return true
    if ((l.customVialName || '').toLowerCase() === lower) return true
    if ((l.shortName || '').toLowerCase() === lower) return true
    return (l.aliases || []).some(function(a) { return a.toLowerCase() === lower })
  })
  if (libMatch) {
    // Find peptide linked to this library entry
    var linked = (S.peptides || []).find(function(p) {
      return (p.name || '').toLowerCase() === (libMatch.scientificName || '').toLowerCase() ||
             (p.name || '').toLowerCase() === (libMatch.customVialName || '').toLowerCase()
    })
    if (linked) return { id:linked.id, method:'Alias', confidence:.95 }
  }

  // P1-5: Fuzzy substring match — only auto-accept if exactly one candidate
  var subMatches = (S.peptides || []).filter(function(p) {
    return (p.name || '').toLowerCase().indexOf(lower) >= 0 || lower.indexOf((p.name || '').toLowerCase()) >= 0
  })
  if (subMatches.length === 1) return { id:subMatches[0].id, method:'Fuzzy', confidence:.75 }
  // Multiple candidates = ambiguous — do not auto-map, leave for review

  return null
}

// ══════════════════════════════════════════════════════
// STEP 1: Parsing indicator
// ══════════════════════════════════════════════════════

function renderMPIParsing() {
  return '<div class="tab-content" style="animation:fadeIn .25s ease">' +
    renderMPIWorkflow('Parse') +
    '<div class="card" style="padding:48px;text-align:center">' +
      '<div style="font-size:32px;margin-bottom:12px">\u23F3</div>' +
      '<p style="font-size:14px">Parsing ' + esc(_mpiFileName) + '...</p>' +
    '</div></div>'
}

// ══════════════════════════════════════════════════════
// STEP 2: Import Summary
// ══════════════════════════════════════════════════════


function mpiEpisodeCount(draft) {
  return _mpiDrafts.filter(function(x) { return x.columnIndex === draft.columnIndex && x.compoundName === draft.compoundName }).length
}

function mpiDisplayName(draft) {
  var count = mpiEpisodeCount(draft)
  return count > 1 ? draft.compoundName + ' — Round ' + (draft.episodeIndex + 1) : draft.compoundName
}

function mpiLegacyAutoName(draft) {
  return draft.compoundName + (draft.episodeIndex > 0 ? ' (' + (draft.episodeIndex + 1) + ')' : '')
}

function mpiNormalizedCompound(value) {
  return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

function mpiIsSharedSupplementSchedule(value) {
  var key = mpiNormalizedCompound(value)
  return key === 'supplements' || key === 'protocolsupplements' || key === 'supplementschedule' || key === 'protocolsupplementschedule'
}

function mpiExactSupplement(value) {
  var key = mpiNormalizedCompound(value)
  if (!key || mpiIsSharedSupplementSchedule(value)) return null
  var matches = (S.supplements || []).filter(function(supplement) { return mpiNormalizedCompound(supplement.name) === key })
  return matches.length === 1 ? matches[0] : null
}

function mpiDraftResolution(draft) {
  var peptideId = _mpiMappings[draft.compoundName]
  if (peptideId) return { kind:'peptide', id:peptideId }
  if (mpiIsSharedSupplementSchedule(draft.compoundName)) return { kind:'supplement-schedule', name:'Protocol supplement schedule' }
  var supplement = mpiExactSupplement(draft.compoundName)
  if (supplement) return { kind:'supplement', id:supplement.id, name:supplement.name }
  return { kind:'unmapped' }
}

// Imported round labels are app-generated, not user edits. They remain safe to
// refresh even when the protocol is currently active.
function mpiHasGeneratedName(protocol, compoundName) {
  if (!protocol) return false
  var name = (protocol.name || '').trim()
  var sourceName = (protocol.sourceColumnName || compoundName || '').trim()
  if (!name || name === sourceName) return true
  var escaped = sourceName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp('^' + escaped + '(?:\\s*[—-]\\s*Round\\s+\\d+|\\s*\\(\\d+\\))$', 'i').test(name)
}

function buildMasterPlanReimportDiff(existing, draft) {
  if (!existing || !draft) return null
  var oldDates = existing.sourceExactSchedule || []
  var newDates = draft.entries.map(function(e){return e.date})
  var oldDose = {}; oldDates.forEach(function(d,i){oldDose[d]=(existing.sourceRawDoseCells||[])[i]||''})
  var newDose = {}; draft.entries.forEach(function(e){newDose[e.date]=e.dose.rawValue})
  var addedDates = newDates.filter(function(d){return oldDates.indexOf(d)<0})
  var removedDates = oldDates.filter(function(d){return newDates.indexOf(d)<0})
  var doseChanges = newDates.filter(function(d){return oldDates.indexOf(d)>=0 && oldDose[d]!==newDose[d]}).map(function(d){return d + ': ' + oldDose[d] + ' → ' + newDose[d]})
  var canonical = mpiDisplayName(draft), legacy = mpiLegacyAutoName(draft)
  var localConflict = !!(existing.name && existing.name !== existing.sourceColumnName && existing.name !== canonical && existing.name !== legacy)
  return { addedDates:addedDates, removedDates:removedDates, doseChanges:doseChanges, startShift:existing.startDate!==draft.startDate, endShift:existing.endDate!==draft.endDate, localConflict:localConflict,
    changed:addedDates.length>0 || removedDates.length>0 || doseChanges.length>0 || existing.startDate!==draft.startDate || existing.endDate!==draft.endDate }
}

function renderMPIWorkflow(active) {
  var steps = ['Upload','Parse','Mapping','AI Review','Protocol Review','Diff','Apply']
  return '<div class="card" style="padding:10px 14px;margin-bottom:14px;display:flex;gap:6px;flex-wrap:wrap">' + steps.map(function(s,i){return '<span class="badge ' + (s===active?'badge-blue':'') + '" style="font-size:10px">' + (i+1) + '. ' + s + '</span>'}).join('') + '</div>'
}

function renderMPISummary() {
  var d = _mpiDetected
  if (!d) return renderMPIDropZone()

  // Check for re-import: same-fingerprint protocols already exist
  var headerSig2 = (d.compoundCols || []).map(function(c) { return c.name.toLowerCase().replace(/[^a-z0-9]/g,'') }).sort().join('|')
  var fingerprint = headerSig2 + '_' + (d.totalRows || 0)
  var existingImported = (S.protocols || []).filter(function(p) { return p.sourceWorkbookFingerprint === fingerprint })
  var isReimport = existingImported.length > 0

  // Diff for re-import
  var diffHtml = ''
  if (isReimport) {
    // P1-6: Episode-level diff using sourceEpisodeKey
    var existingKeys = existingImported.map(function(p) { return { key: p.sourceEpisodeKey, name: p.sourceColumnName || p.name, id: p.id } })
    var newKeys = _mpiDrafts.map(function(d2) {
      var nComp = d2.compoundName.toLowerCase().replace(/[^a-z0-9]/g, '')
      return { key: nComp + ':' + d2.startDate, name: mpiDisplayName(d2), startDate: d2.startDate, endDate: d2.endDate }
    })
    var existingKeySet = existingKeys.map(function(e) { return e.key })
    var newKeySet = newKeys.map(function(n) { return n.key })
    var added = newKeys.filter(function(n) { return existingKeySet.indexOf(n.key) < 0 }).map(function(n) { return n.name })
    var removed = existingKeys.filter(function(e) { return newKeySet.indexOf(e.key) < 0 }).map(function(e) { return e.name })
    var updated = newKeys.filter(function(n) { return existingKeySet.indexOf(n.key) >= 0 }).map(function(n) { return n.name })

    // Item 19: Proper re-import classification table
    diffHtml = '<div class="card" style="margin-top:12px;padding:0;overflow:hidden;border-left:3px solid var(--amber)">' +
      '<div style="padding:10px 14px;background:var(--bg3);border-bottom:1px solid var(--border2);font-size:13px;font-weight:600">\u26A0 Re-import Classification \u2014 ' + existingImported.length + ' previously imported</div>' +
      '<table style="width:100%;border-collapse:collapse;font-size:12px">' +
        '<thead><tr style="border-bottom:1px solid var(--border)"><th style="padding:6px 14px;text-align:left;font-size:10px;font-weight:700;color:var(--text4);text-transform:uppercase">Compound</th><th style="padding:6px 14px;text-align:center;font-size:10px;font-weight:700;color:var(--text4);text-transform:uppercase">Status</th></tr></thead><tbody>' +
        updated.map(function(n) {
          // P1-6: Episode-level hash comparison
          var nKey = newKeys.find(function(nk) { return nk.name === n })
          var existP = nKey ? existingImported.find(function(p) { return p.sourceEpisodeKey === nKey.key }) : existingImported.find(function(p) { return p.sourceColumnName === n })
          var newD = _mpiDrafts.find(function(d) { return mpiDisplayName(d) === n || mpiLegacyAutoName(d) === n || d.compoundName === n })
          var oldHash = existP ? (existP.sourceExactSchedule||[]).map(function(d2,i){return d2+'|'+((existP.sourceRawDoseCells||[])[i]||'')}).join(';') : ''
          var newHash = newD ? newD.entries.map(function(e){return e.date+'|'+e.dose.rawValue}).join(';') : ''
          var diff = buildMasterPlanReimportDiff(existP, newD)
          var changed = diff ? diff.changed : oldHash !== newHash
          var detail = diff && changed ? [diff.addedDates.length + ' added', diff.removedDates.length + ' removed', diff.doseChanges.length + ' dose changes', diff.startShift?'start shifted':'', diff.endShift?'end shifted':'', diff.localConflict?'local name conflict':''].filter(Boolean).join(' · ') : ''
          return '<tr style="border-bottom:1px solid var(--border2)"><td style="padding:6px 14px;font-weight:500">' + esc(n) + (detail ? '<div style="font-size:10px;color:var(--text4);margin-top:2px">' + esc(detail) + '</div>' : '') + '</td><td style="padding:6px 14px;text-align:center"><span class="badge ' + (changed ? 'badge-blue' : 'badge-green') + '">' + (changed ? 'UPDATED' : 'UNCHANGED') + '</span></td></tr>'
        }).join('') +
        added.map(function(n) { return '<tr style="border-bottom:1px solid var(--border2)"><td style="padding:6px 14px;font-weight:500">' + esc(n) + '</td><td style="padding:6px 14px;text-align:center"><span class="badge" style="background:var(--greenL);color:var(--green)">ADDED</span></td></tr>' }).join('') +
        removed.map(function(n) { return '<tr style="border-bottom:1px solid var(--border2)"><td style="padding:6px 14px;font-weight:500">' + esc(n) + '</td><td style="padding:6px 14px;text-align:center"><span class="badge" style="background:var(--redL);color:var(--red)">REMOVED FROM SOURCE</span></td></tr>' }).join('') +
      '</tbody></table></div>'
  }

  return '<div class="tab-content" style="animation:fadeIn .25s ease">' +
    renderMPIWorkflow(isReimport ? 'Diff' : 'Mapping') +
    '<div class="row" style="margin-bottom:16px;align-items:center;gap:8px">' +
      '<h2 style="font-size:20px;font-weight:700">Import Summary</h2>' +
      '<div class="spacer"></div>' +
      '<button class="btn btn-secondary" onclick="resetMasterPlanImport()">Cancel</button>' +
      '<button class="btn btn-secondary" onclick="runMPIAIAnalysis()">' + (_mpiAIRunning ? '\u23F3 Analyzing...' : _mpiAIDone ? '\u2713 AI Analysis Complete' : '\uD83E\uDD16 Analyze with AI') + '</button>' +
      '<button class="btn btn-primary" onclick="setMPIStep(3)">Review Protocols \u2192</button>' +
    '</div>' +

    '<div class="card" style="padding:14px">' +
      '<div style="font-size:13px;font-weight:600;margin-bottom:8px">\uD83D\uDCC4 ' + esc(_mpiFileName) + '</div>' +
      '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:10px">' +
        '<div style="background:var(--bg3);border-radius:var(--r2);padding:10px;text-align:center"><div style="font-size:20px;font-weight:700;color:var(--blue)">' + d.totalRows + '</div><div style="font-size:10px;color:var(--text4)">Data Rows</div></div>' +
        '<div style="background:var(--bg3);border-radius:var(--r2);padding:10px;text-align:center"><div style="font-size:20px;font-weight:700;color:var(--green)">' + d.compoundCols.length + '</div><div style="font-size:10px;color:var(--text4)">Compounds</div></div>' +
        '<div style="background:var(--bg3);border-radius:var(--r2);padding:10px;text-align:center"><div style="font-size:20px;font-weight:700;color:var(--accent)">' + _mpiDrafts.length + '</div><div style="font-size:10px;color:var(--text4)">Protocol Episodes</div></div>' +
        '<div style="background:var(--bg3);border-radius:var(--r2);padding:10px;text-align:center"><div style="font-size:20px;font-weight:700;color:' + (d.anomalies.length > 0 ? 'var(--amber)' : 'var(--green)') + '">' + d.anomalies.length + '</div><div style="font-size:10px;color:var(--text4)">Anomalies</div></div>' +
      '</div>' +
    '</div>' +

    // Column details
    '<div class="card" style="margin-top:12px;padding:14px">' +
      '<div style="font-size:11px;font-weight:700;color:var(--text4);text-transform:uppercase;margin-bottom:6px">Detected Columns</div>' +
      '<div style="font-size:12px;color:var(--text3);margin-bottom:6px">Date column: <b>' + (d.dateCol >= 0 ? 'Column ' + (d.dateCol + 1) + ' (' + esc(d.headers[d.dateCol] || '') + ')' : 'Not found') + '</b></div>' +
      d.compoundCols.map(function(cc) {
        var draftsForCol = _mpiDrafts.filter(function(dd) { return dd.columnIndex === cc.col })
        return '<div style="padding:4px 0;border-bottom:1px solid var(--border2);font-size:12px">' +
          '<span style="font-weight:600">' + esc(cc.name) + '</span> (col ' + (cc.col + 1) + ') \u2014 ' +
          draftsForCol.length + ' episode(s)' +
          (draftsForCol.length > 0 ? ', ' + draftsForCol[0].totalDoses + ' doses, ' + draftsForCol[0].detectedFrequency.label : '') +
          (function(){ var resolution = draftsForCol.length ? mpiDraftResolution(draftsForCol[0]) : {kind:'unmapped'}; return resolution.kind !== 'unmapped' ? ' \u2714 ' + (resolution.kind === 'peptide' ? 'mapped' : 'linked to supplement schedule') : ' <span style="color:var(--amber)">\u2022 unmapped</span>' })() +
        '</div>'
      }).join('') +
    '</div>' +

    // Anomalies
    (d.anomalies.length > 0 ? '<div class="card" style="margin-top:12px;padding:14px;border-left:3px solid var(--amber)">' +
      '<div style="font-size:11px;font-weight:700;color:var(--amber);text-transform:uppercase;margin-bottom:6px">\u26A0 Anomalies</div>' +
      d.anomalies.map(function(a) { return '<div style="font-size:12px;color:var(--text3);padding:2px 0">' + esc(a) + '</div>' }).join('') +
    '</div>' : '') +

    diffHtml +

    // Item 14: AI Summary
    (_mpiAISummary ? '<div class="card" style="margin-top:12px;padding:14px;border-left:3px solid var(--accent)"><div style="font-size:11px;font-weight:700;color:var(--accent);text-transform:uppercase;margin-bottom:4px">\uD83E\uDD16 AI Summary</div><div style="font-size:12px;color:var(--text2)">' + esc(_mpiAISummary) + '</div></div>' : '') +

    // Item 9: AI Mapping Suggestions
    (_mpiAIMappings.length > 0 ? '<div class="card" style="margin-top:12px;padding:14px"><div style="font-size:11px;font-weight:700;color:var(--accent);text-transform:uppercase;margin-bottom:6px">\uD83E\uDD16 AI Mapping Suggestions</div>' +
      _mpiAIMappings.map(function(m) {
        var conf = m.confidence >= 0.9 ? 'High' : m.confidence >= 0.7 ? 'Medium' : 'Low'
        var confColor = m.confidence >= 0.9 ? 'var(--green)' : m.confidence >= 0.7 ? 'var(--amber)' : 'var(--red)'
        return '<div style="padding:6px 0;border-bottom:1px solid var(--border2);font-size:12px"><b>' + esc(m.source) + '</b> \u2192 ' + esc(m.targetName || '?') +
          ' <span style="color:' + confColor + ';font-size:11px">' + conf + ' (' + Math.round((m.confidence||0)*100) + '%)</span>' +
          (m.reasoning ? ' <span style="color:var(--text4);font-size:11px">\u2014 ' + esc(m.reasoning) + '</span>' : '') +
          (m.targetId ? ' <button class="btn btn-xs btn-primary" onclick="acceptMPIMapping(\'' + esc(m.source) + '\',\'' + esc(m.targetId) + '\',\'AI\',' + Number(m.confidence||0) + ')">Accept</button>' : '') +
          ' <button class="btn btn-xs btn-secondary" onclick="event.stopPropagation();showMPIManualMap(\'' + esc(m.source) + '\')">Choose Different</button>' +
          ' <button class="btn btn-xs btn-secondary" onclick="event.stopPropagation();showToast(\'Add this peptide in the Peptides tab, then re-import\')">Create New</button>' +
        '</div>'
      }).join('') +
    '</div>' : '') +

    // Item 10: AI Combination Suggestions
    (_mpiAICombinations.length > 0 ? '<div class="card" style="margin-top:12px;padding:14px"><div style="font-size:11px;font-weight:700;color:var(--accent);text-transform:uppercase;margin-bottom:6px">\uD83E\uDD16 Possible Combination Protocols</div>' +
      _mpiAICombinations.map(function(g) {
        return '<div style="padding:6px 0;border-bottom:1px solid var(--border2);font-size:12px">' +
          '<b>' + (g.compounds || []).join(' + ') + '</b> <span style="color:var(--text3);font-size:11px">\u2014 ' + esc(g.reason || '') + ' (' + Math.round((g.confidence||0)*100) + '%)</span>' +
          ' <button class="btn btn-xs btn-primary" onclick="combineMPIDrafts([' + (g.compounds||[]).map(function(c){return "'"+esc(c)+"'"}).join(',') + '])">Combine</button>' +
          ' <button class="btn btn-xs btn-secondary" onclick="showToast(\'Keeping as separate protocols\')">Keep Separate</button>' +
          ' <button class="btn btn-xs btn-ghost" onclick="saveGroupingRule([' + (g.compounds||[]).map(function(c){return "'"+esc(c)+"'"}).join(',') + '])">Always Combine These</button>' +
        '</div>'
      }).join('') +
    '</div>' : '') +

    // Item 12: AI Anomalies
    (_mpiAIAnomalies.length > 0 ? '<div class="card" style="margin-top:12px;padding:14px;border-left:3px solid var(--amber)"><div style="font-size:11px;font-weight:700;color:var(--amber);text-transform:uppercase;margin-bottom:6px">\u26A0 AI-Detected Anomalies</div>' +
      _mpiAIAnomalies.map(function(a) {
        var sevColor = a.severity === 'high' ? 'var(--red)' : a.severity === 'medium' ? 'var(--amber)' : 'var(--text3)'
        return '<div style="padding:4px 0;font-size:12px"><span style="color:' + sevColor + ';font-weight:600">' + esc(a.compound || '') + '</span>' +
          (a.date ? ' <span style="color:var(--text4)">' + a.date + '</span>' : '') +
          ' \u2014 ' + esc(a.issue || '') +
          ' <button class="btn btn-xs btn-secondary" style="margin-left:6px" onclick="showToast(\'Keeping as-is\')">Keep</button>' +
          ' <button class="btn btn-xs btn-secondary" onclick="excludeMPIDate(\'' + esc(a.compound||'') + '\',\'' + esc(a.date||'') + '\')">Exclude</button>' +
          ' <button class="btn btn-xs btn-secondary" onclick="showToast(\'Edit in the Review step\')">Edit</button>' +
        '</div>'
      }).join('') +
    '</div>' : '') +
  '</div>'
}

// ══════════════════════════════════════════════════════
// STEP 3: Review Protocols
// ══════════════════════════════════════════════════════

function renderMPIReview() {
  var peptides = (S.peptides || []).filter(function(p) { return !p.archived })
  var unresolvedNames = []
  _mpiDrafts.forEach(function(draft) {
    if (mpiDraftResolution(draft).kind === 'unmapped' && unresolvedNames.indexOf(draft.compoundName) < 0) unresolvedNames.push(draft.compoundName)
  })
  unresolvedNames.sort(function(a,b) { return a.localeCompare(b) })
  var reviewDrafts = _mpiDrafts.slice().sort(function(a,b) {
    var aMissing = mpiDraftResolution(a).kind === 'unmapped' ? 0 : 1
    var bMissing = mpiDraftResolution(b).kind === 'unmapped' ? 0 : 1
    return aMissing - bMissing || a.compoundName.localeCompare(b.compoundName) || a.startDate.localeCompare(b.startDate)
  })

  return '<div class="tab-content" style="animation:fadeIn .25s ease">' +
    renderMPIWorkflow('Protocol Review') +
    '<div class="row" style="margin-bottom:16px;align-items:center;gap:8px">' +
      '<h2 style="font-size:20px;font-weight:700">Review Protocols</h2>' +
      '<span style="font-size:12px;color:var(--text3)">' + _mpiDrafts.length + ' protocol episodes</span>' +
      (unresolvedNames.length ? '<span class="badge" style="background:var(--amberL);color:var(--amber)">' + unresolvedNames.length + ' need mapping</span>' : '<span class="badge badge-green">All items resolved</span>') +
      '<div class="spacer"></div>' +
      '<button class="btn btn-secondary" onclick="setMPIStep(2)">\u2190 Back</button>' +
      '<button class="btn btn-primary" onclick="applyMasterPlanImport()">Apply Import \u2714</button>' +
    '</div>' +

    renderMasterPlanImportImpactPreview() +

    // Item 13: AI Note Interpretations
    (_mpiAINotes.length > 0 ? '<div class="card" style="margin-top:12px;padding:14px"><div style="font-size:11px;font-weight:700;color:var(--accent);text-transform:uppercase;margin-bottom:6px">\uD83D\uDCDD AI Note Interpretations</div>' +
      _mpiAINotes.map(function(n) {
        return '<div style="padding:4px 0;border-bottom:1px solid var(--border2);font-size:12px">' +
          '<div style="color:var(--text3)">"' + esc(n.noteText || '') + '"</div>' +
          '<div style="margin-top:2px"><b>' + esc(n.interpretation || '') + '</b>' +
          (n.dependencyType && n.dependencyType !== 'none' ? ' <span class="badge" style="font-size:9px">' + esc(n.dependencyType) + '</span>' : '') +
          (n.relatedCompound ? ' \u2192 ' + esc(n.relatedCompound) : '') +
          ' <span style="color:var(--text4);font-size:11px">(' + Math.round((n.confidence||0)*100) + '%)</span>' +
          (n.dependencyType && n.dependencyType !== 'none' ? ' <button class="btn btn-xs btn-primary" onclick="applyDependency(\'' + esc(n.dependencyType||'') + '\',\'' + esc(n.relatedCompound||'') + '\',' + (n.offsetDays||0) + ',\'' + esc(n.interpretation||'') + '\')">Apply Dependency</button>' : '') +
          ' <button class="btn btn-xs btn-secondary" onclick="this.closest(\'.card\').querySelector(\'[style*=border-bottom]\')&&this.parentElement.parentElement.remove();showToast(\'Ignored\')">Ignore</button>' +
          ' <button class="btn btn-xs btn-ghost" onclick="showToast(\'Edit dependency in Protocol Planner after import\')">Edit</button>' +
        '</div></div>'
      }).join('') +
    '</div>' : '') +


    '<div style="font-size:12px;color:var(--text3);margin-bottom:12px">Items needing attention are grouped first. Supplement schedules are linked automatically and do not require a peptide mapping.</div>' +

    (unresolvedNames.length ? '<div class="card" style="padding:14px;margin-bottom:14px;border-left:4px solid var(--amber)">' +
      '<div style="font-size:13px;font-weight:700;color:var(--amber);margin-bottom:8px">Needs Mapping</div>' +
      unresolvedNames.map(function(name) {
        var episodes = _mpiDrafts.filter(function(draft) { return draft.compoundName === name }).length
        return '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px;align-items:center;padding:7px 0;border-bottom:1px solid var(--border2)">' +
          '<div><b>' + esc(name) + '</b><div style="font-size:10px;color:var(--text4)">' + episodes + ' episode' + (episodes === 1 ? '' : 's') + '</div></div>' +
          '<select class="form-input" style="font-size:12px" onchange="setMPIMapping(\'' + esc(name) + '\',this.value)">' +
            '<option value="">\u2014 Choose peptide \u2014</option>' + peptides.map(function(p) { return '<option value="' + p.id + '">' + esc(p.name) + '</option>' }).join('') +
          '</select></div>'
      }).join('') + '</div>' : '') +

    reviewDrafts.map(function(d, i) {
      var mapped = _mpiMappings[d.compoundName]
      var mappedPep = mapped ? peptides.find(function(p) { return p.id === mapped }) : null
      var mappingMeta = _mpiMappingMeta[d.compoundName]
      var resolution = mpiDraftResolution(d)
      var isSchedule = resolution.kind === 'supplement-schedule'
      var isSupplement = resolution.kind === 'supplement'
      var isUnmapped = resolution.kind === 'unmapped'

      return '<div class="card" style="margin-bottom:10px;padding:0;overflow:hidden' + (!_mpiIncluded[d.id] ? ';opacity:0.5' : '') + (isUnmapped ? ';border-left:4px solid var(--amber)' : '') + '">' +
        '<div style="padding:10px 14px;display:flex;gap:10px;align-items:center;border-bottom:1px solid var(--border2)">' +
          '<input type="checkbox" ' + (_mpiIncluded[d.id] ? 'checked' : '') + ' onchange="_mpiIncluded[\'' + d.id + '\']=this.checked;render()">' +
          '<div style="flex:1">' +
            '<div style="font-size:14px;font-weight:600">' + esc(d.compoundName) + (d.episodeIndex > 0 ? ' <span style="color:var(--text4);font-size:11px">(episode ' + (d.episodeIndex + 1) + ')</span>' : '') +
              (function(){ var aiName = (_mpiAINames||[]).find(function(n){return n.raw === d.compoundName + (d.episodeIndex > 0 ? '_' + d.episodeIndex : '')}); return aiName ? ' <span style="font-size:11px;color:var(--accent)" title="AI suggestion">\u2192 ' + esc(aiName.suggested) + ' <button class="btn btn-xs btn-ghost" onclick="event.stopPropagation();acceptAIName(\'' + esc(d.id) + '\',\'' + esc(aiName.suggested).replace(/'/g,"\\'") + '\')">\u2713</button></span>' : '' })() +
            '</div>' +
            '<div style="font-size:12px;color:var(--text3)">' + d.startDate + ' \u2192 ' + d.endDate + ' \u00B7 ' + d.totalDoses + (isSchedule ? ' scheduled days' : ' doses') + ' \u00B7 ' + d.detectedFrequency.label + (isSchedule ? '' : ' \u00B7 ' + d.doseAmount + ' ' + d.doseUnit) + '</div>' +
            (mappingMeta ? '<div style="margin-top:3px"><span class="badge" style="font-size:9px">' + esc(mappingMeta.method) + ' · ' + Math.round(mappingMeta.confidence*100) + '%</span> <span style="font-size:10px;color:var(--text4)">Sheet ' + esc((_mpiDetected&&_mpiDetected.sheetName)||'Sheet1') + ' · Column ' + (d.columnIndex+1) + '</span></div>' : '') +
            (isSchedule ? '<div style="margin-top:3px"><span class="badge badge-green" style="font-size:9px">Shared supplement schedule</span></div>' : '') +
            (isSupplement ? '<div style="margin-top:3px"><span class="badge badge-green" style="font-size:9px">Supplement · ' + esc(resolution.name) + '</span></div>' : '') +
          '</div>' +
          '<div style="min-width:180px">' +
            (isSchedule || isSupplement ? '<div style="font-size:12px;color:var(--green);font-weight:600;text-align:right">Linked automatically</div>' : '<select class="form-input" style="font-size:12px" onchange="setMPIMapping(\'' + esc(d.compoundName) + '\',this.value)">' +
              '<option value="">\u2014 Map to Peptide \u2014</option>' +
              peptides.map(function(p) {
                return '<option value="' + p.id + '"' + (mapped === p.id ? ' selected' : '') + '>' + esc(p.name) + '</option>'
              }).join('') +
            '</select>') +
          '</div>' +
        '</div>' +

        // Detail: phases, breaks, sample dates
        '<div style="padding:8px 14px;font-size:12px;color:var(--text3);display:flex;gap:16px;flex-wrap:wrap">' +
          (isSchedule ? '<div>Activation schedule for linked protocol supplements</div>' : (d.phases.length > 0 ? '<div>' + d.phases.length + ' phases: ' + d.phases.map(function(ph) { return ph.doseAmount + ' ' + ph.doseUnit + ' (days ' + ph.startDay + '\u2013' + ph.endDay + ')' }).join(', ') + '</div>' : '<div>Single dose throughout</div>')) +
          (d.breaks.length > 0 ? '<div style="color:var(--amber)">' + d.breaks.length + ' break(s) detected</div>' : '') +
          '<div>First 5 dates: ' + d.sourceExactSchedule.slice(0, 5).map(function(dt) { return dt.slice(5) }).join(', ') + (d.sourceExactSchedule.length > 5 ? '...' : '') + '</div>' +
        '</div>' +
      '</div>'
    }).join('') +
  '</div>'
}

function buildMasterPlanImportImpactPreview() {
  var included=_mpiDrafts.filter(function(draft){return !!_mpiIncluded[draft.id]})
  var incoming={}
  var rows=[]
  included.forEach(function(draft){
    var key=mpiNormalizedCompound(draft.compoundName)+':'+(draft.startDate||'')
    incoming[key]=true
    var existing=(S.protocols||[]).find(function(protocol){return protocol.sourceType==='excel-master-plan'&&protocol.sourceEpisodeKey===key})
    var diff=existing?buildMasterPlanReimportDiff(existing,draft):null
    rows.push({type:existing?(diff&&diff.changed?'changed':'unchanged'):'added',name:mpiDisplayName(draft),protocolId:existing&&existing.id||'',detail:diff&&diff.changed?[diff.addedDates.length+' dates added',diff.removedDates.length+' dates removed',diff.doseChanges.length+' dose changes',diff.startShift?'start shifted':'',diff.endShift?'end shifted':''].filter(Boolean).join(' · '):existing?'No schedule changes':'New protocol episode'})
  })
  ;(S.protocols||[]).filter(function(protocol){return protocol.sourceType==='excel-master-plan'&&!protocol.archived&&protocol.sourceEpisodeKey&&!incoming[protocol.sourceEpisodeKey]}).forEach(function(protocol){rows.push({type:'removed',name:protocol.name||protocol.peptideName||'Protocol',protocolId:protocol.id,detail:'No longer present in the imported schedule; retained in history and retired'})})
  var affectedIds=new Set(rows.filter(function(row){return row.protocolId&&row.type!=='unchanged'}).map(function(row){return row.protocolId}))
  var affectedReservations=(S.inventoryReservations||[]).filter(function(row){return affectedIds.has(row.protocolId)})
  var completedPicks=affectedReservations.filter(function(row){return row.pickConfirmedAt||['picked','in_use','completed'].includes(row.workflowStatus)})
  return {rows:rows,added:rows.filter(function(row){return row.type==='added'}).length,changed:rows.filter(function(row){return row.type==='changed'}).length,removed:rows.filter(function(row){return row.type==='removed'}).length,affectedReservations:affectedReservations.length,completedPicks:completedPicks.length,completedPicksPreserved:true}
}

function setMPIImpactAcknowledged(value){_mpiImpactAcknowledged=!!value;render()}

function renderMasterPlanImportImpactPreview() {
  var impact=buildMasterPlanImportImpactPreview()
  return '<section class="v9-import-impact v9-surface"><header><div><span class="v9-kicker">Before apply</span><h3>Import impact</h3><p>Schedule changes never erase completed picks or silently restore inventory.</p></div></header><div class="v9-impact-stats"><span><b>'+impact.added+'</b>Added</span><span><b>'+impact.changed+'</b>Changed</span><span><b>'+impact.removed+'</b>Removed</span><span><b>'+impact.affectedReservations+'</b>Assignments to review</span></div><div class="v9-impact-list">'+(impact.rows.length?impact.rows.map(function(row){return '<div><b>'+esc(row.name)+'</b><span>'+esc(row.detail)+'</span><em>'+esc(row.type)+'</em></div>'}).join(''):'<div><span>No included protocol episodes.</span></div>')+'</div>'+(impact.completedPicks?'<div class="v9-empty-inline">'+impact.completedPicks+' completed or in-progress pick'+(impact.completedPicks===1?' is':'s are')+' preserved as history. Affected assignments are flagged for review.</div>':'')+'<label class="v9-impact-confirm"><input type="checkbox" '+(_mpiImpactAcknowledged?'checked':'')+' onchange="setMPIImpactAcknowledged(this.checked)"><span>I reviewed these additions, changes, removals, and assignment impacts.</span></label></section>'
}

function setMPIMapping(compoundName, peptideId) {
  if (peptideId) { _mpiMappings[compoundName] = peptideId; _mpiMappingMeta[compoundName] = { method:'Manual', confidence:1 } }
  else { delete _mpiMappings[compoundName]; delete _mpiMappingMeta[compoundName] }
  render()
}
function acceptMPIMapping(compoundName, peptideId, method, confidence) { _mpiMappings[compoundName] = peptideId; _mpiMappingMeta[compoundName] = { method:method||'Manual', confidence:Number(confidence)||1 }; render() }

// ══════════════════════════════════════════════════════
// STEP 4: Apply Import
// ══════════════════════════════════════════════════════

function applyMasterPlanImport() {
  if (!_mpiImpactAcknowledged) { showToast('Review and acknowledge the import impact before applying','error'); return }
  // P2-7: Stable workbook identity from normalized header set
  var headerSig = (_mpiDetected.compoundCols || []).map(function(c) { return c.name.toLowerCase().replace(/[^a-z0-9]/g,'') }).sort().join('|')
  var fingerprint = headerSig + '_' + (_mpiDetected.totalRows || 0)
  var importedAt = now()
  var applied = 0
  var updated = 0
  var skipped = 0
  var superseded = 0
  var importedKeysByCompound = {}
  var firstImportedDateByCompound = {}
  var fullyIncludedCompounds = {}

  ;(_mpiDetected.compoundCols || []).forEach(function(c) {
    var key = mpiNormalizedCompound(c.name)
    var drafts = _mpiDrafts.filter(function(d) { return mpiNormalizedCompound(d.compoundName) === key })
    fullyIncludedCompounds[key] = drafts.length > 0 && drafts.every(function(d) { return !!_mpiIncluded[d.id] })
  })

  _mpiDrafts.forEach(function(d) {
    if (!_mpiIncluded[d.id]) return

    var peptideId = _mpiMappings[d.compoundName] || ''
    var supplementResolution = mpiDraftResolution(d)
    var supplementId = supplementResolution.kind === 'supplement' ? supplementResolution.id : ''
    var isSupplementSchedule = supplementResolution.kind === 'supplement' || supplementResolution.kind === 'supplement-schedule'
    var peptideName = ''
    if (peptideId) {
      var pep = (S.peptides || []).find(function(p) { return p.id === peptideId })
      if (pep) peptideName = pep.name
    }

    // P0-5: Stable episode identity for re-import sync
    // Item 17: Stable identity using anchor date, not episode index
    var normalizedCompound = mpiNormalizedCompound(d.compoundName)
    var episodeAnchor = d.startDate || ''
    var episodeKey = normalizedCompound + ':' + episodeAnchor
    if (!importedKeysByCompound[normalizedCompound]) importedKeysByCompound[normalizedCompound] = {}
    importedKeysByCompound[normalizedCompound][episodeKey] = true
    if (!firstImportedDateByCompound[normalizedCompound] || d.startDate < firstImportedDateByCompound[normalizedCompound]) firstImportedDateByCompound[normalizedCompound] = d.startDate
    var existing = (S.protocols || []).find(function(p) {
      return p.sourceType === 'excel-master-plan' && p.sourceEpisodeKey === episodeKey
    })

    // P1-4: Build explicit schedule entries with per-date doses
    var explicitEntries = d.entries.map(function(e) {
      return { date: e.date, time: e.time || '', timeLabel: e.timeLabel || '', administrationIndex: e.administrationIndex == null ? null : e.administrationIndex, doseAmount: e.dose.numericValue, doseUnit: e.dose.unit || d.doseUnit, rawValue: e.dose.rawValue, sourceRawCell: e.sourceRawCell || e.dose.rawValue, components: e.dose.components || null, sourceRow:e.sourceRow, sourceColumn:e.sourceColumn }
    })

    if (existing) {
      var restoredFromSuperseded = !!existing.supersededByImport
      if (restoredFromSuperseded) {
        existing.archived = false
        existing.supersededByImport = false
        existing.supersededAt = ''
        existing.status = 'upcoming'
      }
      // Re-import: compute content hash to detect changes
      // Item 18: Hash all explicit entries (date+dose+unit+components) for precise change detection
      var oldHash = (existing.sourceExactSchedule || []).map(function(d2,i) { return d2 + '|' + ((existing.sourceRawDoseCells||[])[i]||'') }).join(';')
      var newHash = d.entries.map(function(e) { return e.date + '|' + e.dose.rawValue }).join(';')
      if (oldHash === newHash) {
        existing.isSupplementSchedule = isSupplementSchedule
        existing.supplementId = supplementId
        if (isSupplementSchedule) {
          existing.status = 'supplement'
          existing.sourceMappingMethod = supplementId ? 'Supplement exact' : 'Supplement schedule'
          existing.sourceMappingConfidence = 1
        }
        // Naming-only migration: upgrade importer-generated episode names to Round labels without touching user-custom names.
        var canonicalName = mpiDisplayName(d)
        var legacyName = mpiLegacyAutoName(d)
        if (existing.name === existing.sourceColumnName || existing.name === legacyName) existing.name = canonicalName
        if (restoredFromSuperseded) updated++
        else skipped++
        return
      } // No schedule changes
      // Update existing protocol (preserve user edits to status/name if changed)
      if (existing.status === 'upcoming' || existing.status === 'draft' || mpiHasGeneratedName(existing, d.compoundName) || existing.name === mpiLegacyAutoName(d) || existing.name === mpiDisplayName(d)) {
        existing.startDate = d.startDate
        existing.endDate = d.endDate
        existing.frequency = d.frequency
        existing.doseAmount = d.doseAmount
        existing.doseUnit = d.doseUnit
        existing.phases = d.phases
        existing.breaks = d.breaks
        existing.sourceExactSchedule = d.sourceExactSchedule
        existing.sourceRawDoseCells = d.sourceRawDoseCells
        existing.sourceRowNumbers = d.entries.map(function(e){return e.sourceRow})
        existing.sourceImportedAt = importedAt
        existing.recognizedPattern = d.detectedFrequency.label
        existing.scheduleMode = 'explicit'
        existing.explicitEntries = explicitEntries
        existing.sourceWorkbookName = _mpiFileName
        existing.sourceSheetName = (_mpiDetected && _mpiDetected.sheetName) || 'Sheet1'
        existing.sourceColumnIndex = d.columnIndex + 1
        existing.sourceMappingMethod = (_mpiMappingMeta[d.compoundName] || {}).method || 'Manual'
        existing.sourceMappingConfidence = (_mpiMappingMeta[d.compoundName] || {}).confidence || 0
        existing.isSupplementSchedule = isSupplementSchedule
        existing.supplementId = supplementId
        if (isSupplementSchedule) {
          existing.status = 'supplement'
          existing.sourceMappingMethod = supplementId ? 'Supplement exact' : 'Supplement schedule'
          existing.sourceMappingConfidence = 1
        }
        existing.updatedAt = importedAt
        if ((S.inventoryReservations||[]).some(function(row){return row.protocolId===existing.id})) existing.reservationReviewRequired = true
        if (peptideId) { existing.peptideId = peptideId; existing.peptideName = peptideName || d.compoundName }
        updated++
      } else {
        // User has locally edited — skip to avoid overwriting their changes
        skipped++
      }
      return
    }

    // New protocol
    var protocol = {
      id: uid(),
      name: mpiDisplayName(d),
      peptideId: peptideId,
      peptideName: peptideName || d.compoundName,
      status: 'upcoming',
      startDate: d.startDate,
      endDate: d.endDate,
      frequency: d.frequency,
      doseAmount: d.doseAmount,
      doseUnit: d.doseUnit,
      weekdays: [],
      phases: d.phases,
      breaks: d.breaks,
      notes: 'Imported from ' + _mpiFileName,
      createdAt: importedAt,
      // P1-4: Explicit schedule mode
      scheduleMode: 'explicit',
      explicitEntries: explicitEntries,
      // Source provenance
      sourceType: 'excel-master-plan',
      sourceWorkbookName: _mpiFileName,
      sourceWorkbookFingerprint: fingerprint,
      sourceSheetName: (_mpiDetected && _mpiDetected.sheetName) || 'Sheet1',
      sourceColumnName: d.compoundName,
      sourceColumnIndex: d.columnIndex + 1,
      sourceRowNumbers: d.entries.map(function(e){return e.sourceRow}),
      sourceMappingMethod: (_mpiMappingMeta[d.compoundName] || {}).method || (peptideId ? 'Manual' : 'Unresolved'),
      sourceMappingConfidence: (_mpiMappingMeta[d.compoundName] || {}).confidence || 0,
      sourceEpisodeKey: episodeKey,
      sourceImportedAt: importedAt,
      sourceExactSchedule: d.sourceExactSchedule,
      sourceRawDoseCells: d.sourceRawDoseCells,
      recognizedPattern: d.detectedFrequency.label,
      recognizedPatternConfidence: d.detectedFrequency.avgGap > 0 ? 'high' : 'low'
    }
    protocol.isSupplementSchedule = isSupplementSchedule
    protocol.supplementId = supplementId
    if (isSupplementSchedule) {
      protocol.status = 'supplement'
      protocol.sourceMappingMethod = supplementId ? 'Supplement exact' : 'Supplement schedule'
      protocol.sourceMappingConfidence = 1
    }

    // Fix 2: Store multi-component data if combined
    if (d.isMultiComponent && d.components) {
      protocol.isMultiComponent = true
      protocol.components = d.components.map(function(c) {
        return { compoundName: c.compoundName, peptideId: c.peptideId || _mpiMappings[c.compoundName] || '', doseAmount: c.doseAmount, doseUnit: c.doseUnit, totalDoses: c.totalDoses, explicitEntries: c.explicitEntries }
      })
    }

    inheritProtocolDisplayPreferences(protocol, S.protocols)
    S.protocols.push(protocol)
    // Fix 4: Commit pending dependencies
    if (d.pendingDependencies && d.pendingDependencies.length > 0) {
      d.pendingDependencies.forEach(function(dep) {
        S.protocolDependencies.push({
          id: uid(), protocolId: protocol.id, relationType: dep.relationType,
          relatedCompound: dep.relatedCompound, relatedProtocolId: '',
          offsetDays: dep.offsetDays, description: dep.description, source: dep.source
        })
      })
    }
    applied++
  })

  // Treat every fully included compound column as authoritative from its first
  // scheduled date forward. Older imported rounds that disappeared from the new
  // workbook are retained for audit, but archived so they cannot remain in the
  // planner or compete with the replacement schedule.
  Object.keys(importedKeysByCompound).forEach(function(compoundKey) {
    if (!fullyIncludedCompounds[compoundKey]) return
    var firstDate = firstImportedDateByCompound[compoundKey] || ''
    ;(S.protocols || []).forEach(function(protocol) {
      if (protocol.sourceType !== 'excel-master-plan' || protocol.archived) return
      if (mpiNormalizedCompound(protocol.sourceColumnName || protocol.peptideName || protocol.name) !== compoundKey) return
      if (!protocol.sourceEpisodeKey || importedKeysByCompound[compoundKey][protocol.sourceEpisodeKey]) return
      if (!firstDate || (protocol.startDate || '') < firstDate) return
      if (!mpiHasGeneratedName(protocol, protocol.sourceColumnName || protocol.peptideName)) return
      protocol.archived = true
      protocol.supersededByImport = true
      protocol.supersededAt = importedAt
      protocol.status = 'completed'
      protocol.completedAt = importedAt
      protocol.updatedAt = importedAt
      if ((S.inventoryReservations||[]).some(function(row){return row.protocolId===protocol.id})) protocol.reservationReviewRequired = true
      superseded++
    })
  })

  var supplementLinks = typeof window.reconcileProtocolSupplementLinksFromImport === 'function' ? window.reconcileProtocolSupplementLinksFromImport(importedAt) : 0
  if (applied > 0 || updated > 0 || superseded > 0 || supplementLinks > 0) {
    save()
    var msg = []
    if (applied > 0) msg.push(applied + ' new')
    if (updated > 0) msg.push(updated + ' updated')
    if (superseded > 0) msg.push(superseded + ' old round' + (superseded === 1 ? '' : 's') + ' retired')
    if (supplementLinks > 0) msg.push(supplementLinks + ' protocol supplement link' + (supplementLinks === 1 ? '' : 's'))
    if (skipped > 0) msg.push(skipped + ' unchanged')
    showToast('Import: ' + msg.join(', '))
  } else if (skipped > 0) {
    showToast('No changes \u2014 ' + skipped + ' protocol(s) unchanged')
  }
  _mpiStep = 4
  _mpiImpactAcknowledged = false
  render()
}

function renderMPIApplied() {
  var included = _mpiDrafts.filter(function(d) { return _mpiIncluded[d.id] })
  return '<div class="tab-content" style="animation:fadeIn .25s ease">' +
    '<div class="card" style="padding:32px;text-align:center">' +
      '<div style="font-size:48px;margin-bottom:12px">\u2705</div>' +
      '<p style="font-size:16px;font-weight:600">Import Complete</p>' +
      '<p style="font-size:13px;color:var(--text3)">' + included.length + ' protocol(s) imported as drafts from ' + esc(_mpiFileName) + '.</p>' +
      '<p style="font-size:12px;color:var(--text4)">Review and activate them in the Protocol Planner.</p>' +
      '<div style="display:flex;gap:8px;justify-content:center;margin-top:16px">' +
        '<button class="btn btn-primary" onclick="setTab(\'planner\')">Open Planner</button>' +
        '<button class="btn btn-secondary" onclick="resetMasterPlanImport()">Import Another</button>' +
      '</div>' +
    '</div>' +
  '</div>'
}

// P1-3: Persistent grouping rule
function saveGroupingRule(compounds) {
  // Fix 3: Persist to S.masterPlanRules (survives restart + sync)
  S.masterPlanRules.push({ id: uid(), type: 'grouping', compounds: compounds, createdAt: now() })
  save()
  combineMPIDrafts(compounds)
  showToast('Grouping rule saved permanently')
}

// P1-3: Apply saved grouping rules during import
function applyGroupingRules() {
  var rules = (S.masterPlanRules || []).filter(function(r) { return r.type === 'grouping' })
  rules.forEach(function(rule) {
    var names = rule.compounds || []
    var matching = _mpiDrafts.filter(function(d) { return names.indexOf(d.compoundName) >= 0 && _mpiIncluded[d.id] })
    if (matching.length >= 2) combineMPIDrafts(names)
  })
}

// P1-3: Accept AI-suggested name for a draft
function acceptAIName(draftId, suggestedName) {
  var draft = _mpiDrafts.find(function(d) { return d.id === draftId })
  if (draft) { draft.compoundName = suggestedName; showToast('Name accepted: ' + suggestedName); render() }
}


// Fix 4: Create real protocol dependency from AI note interpretation
function applyDependency(relationType, relatedCompound, offsetDays, description) {
  // P1-3: Find target by relatedCompound match first; if ambiguous, use the first draft that references the compound
  var targetDraft = null
  // Look for a draft whose name matches the related compound (dependency target)
  if (relatedCompound) {
    var relatedDraft = _mpiDrafts.find(function(d) { return _mpiIncluded[d.id] && d.compoundName.toLowerCase().indexOf(relatedCompound.toLowerCase()) >= 0 })
    // The dependency goes ON the protocol that depends on relatedCompound, not relatedCompound itself
    // Find another draft that is NOT the related compound
    var candidates = _mpiDrafts.filter(function(d) { return _mpiIncluded[d.id] && d.compoundName !== relatedCompound && d.compoundName.toLowerCase() !== relatedCompound.toLowerCase() })
    if (candidates.length === 1) targetDraft = candidates[0]
    else if (candidates.length > 1) { showToast('Multiple protocols could be the dependency target — select manually in Protocol Planner after import'); return }
    else targetDraft = relatedDraft // fallback: apply to the related compound itself
  }
  if (!targetDraft) { showToast('Could not determine target protocol'); return }
  // Store as a pending dependency — applied to the protocol when import is committed
  if (!targetDraft.pendingDependencies) targetDraft.pendingDependencies = []
  targetDraft.pendingDependencies.push({
    relationType: relationType,
    relatedCompound: relatedCompound,
    offsetDays: offsetDays || 0,
    description: description,
    source: 'ai'
  })
  showToast('Dependency applied: ' + relationType + ' ' + relatedCompound)
  render()
}


// Item 9: Manual mapping popup
function showMPIManualMap(compoundName) {
  var peptides = (S.peptides || []).filter(function(p) { return !p.archived })
  var html = peptides.map(function(p) { return '<div style="padding:4px 0;cursor:pointer;font-size:12px" onclick="_mpiMappings[\'' + esc(compoundName) + '\']=\'' + p.id + '\';closeModal();render()">' + esc(p.name) + '</div>' }).join('')
  openModal('aiExplain', { title: 'Map: ' + compoundName, content: 'Select a peptide:\n' + (peptides.length > 0 ? '(click one)' : 'No peptides available — add in Peptides tab first') })
}

// Item 10: Combine drafts into one
function combineMPIDrafts(compoundNames) {
  if (compoundNames.length < 2) return
  // Collect all matching drafts
  var matching = _mpiDrafts.filter(function(d) { return compoundNames.indexOf(d.compoundName) >= 0 && _mpiIncluded[d.id] })
  if (matching.length < 2) return

  // Build multi-component protocol: first draft becomes the container, others become components
  var primary = matching[0]
  var components = matching.map(function(d) {
    return {
      compoundName: d.compoundName,
      peptideId: _mpiMappings[d.compoundName] || '',
      doseAmount: d.doseAmount,
      doseUnit: d.doseUnit,
      totalDoses: d.totalDoses,
      explicitEntries: d.entries.map(function(e) {
        return { date: e.date, doseAmount: e.dose.numericValue, doseUnit: e.dose.unit || d.doseUnit, rawValue: e.dose.rawValue, components: e.dose.components || null }
      })
    }
  })

  // Update the primary draft to carry all components
  primary.compoundName = compoundNames.join(' + ')
  primary.components = components
  primary.isMultiComponent = true

  // Exclude the other drafts (but data preserved in components array)
  for (var i = 1; i < matching.length; i++) {
    _mpiIncluded[matching[i].id] = false
  }

  showToast('Combined: ' + compoundNames.join(' + ') + ' (' + components.length + ' components)')
  render()
}

// Item 12: Exclude a specific date from a draft
function excludeMPIDate(compound, date) {
  _mpiDrafts.forEach(function(d) {
    if (d.compoundName === compound) {
      d.entries = d.entries.filter(function(e) { return e.date !== date })
      d.sourceExactSchedule = d.sourceExactSchedule.filter(function(dt) { return dt !== date })
      d.totalDoses = d.entries.length
    }
  })
  showToast('Excluded ' + date + ' from ' + compound)
  render()
}


export {
  _mpiStep,
  _mpiRows,
  _mpiDetected,
  _mpiDrafts,
  _mpiMappings,
  _mpiIncluded,
  _mpiFileName,
  _mpiError,
  resetMasterPlanImport,
  renderMasterPlanImportTab,
  setMPIStep,
  renderMPIDropZone,
  triggerMPIFileSelect,
  handleMPIFile,
  detectMasterPlanStructure,
  parseMPDate,
  parseDoseCell,
  parseSupplementScheduleCell,
  parseTimedDoseCell,
  buildProtocolDrafts,
  splitEpisodes,
  detectFrequency,
  detectPhases,
  detectBreaks,
  autoMapCompound,
  autoMapCompoundDetailed,
  mpiIsSharedSupplementSchedule,
  mpiExactSupplement,
  mpiDraftResolution,
  buildMasterPlanReimportDiff,
  renderMPIWorkflow,
  renderMPIParsing,
  renderMPISummary,
  renderMPIReview,
  buildMasterPlanImportImpactPreview,
  renderMasterPlanImportImpactPreview,
  setMPIImpactAcknowledged,
  setMPIMapping,
  acceptMPIMapping,
  applyMasterPlanImport,
  renderMPIApplied,
  renderMPIExistingImports,
  showMPIManualMap,
  combineMPIDrafts,
  excludeMPIDate,
  saveGroupingRule,
  applyGroupingRules,
  acceptAIName,
  applyDependency,
  runMPIAIAnalysis,
  _mpiAIRunning,
  _mpiAIDone
}
